import React from 'react';
import { motion } from 'framer-motion';
import SEO from '../components/common/SEO';
import Breadcrumb from '../components/common/Breadcrumb';

function TermsPage() {
  const breadcrumbItems = [
    { name: 'Terms of Service' }
  ];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="min-h-screen bg-gray-50 py-8"
    >
      <SEO
        title="Terms of Service - Pokemon Stats Database"
        description="Terms of Service and User Agreement for Pokemon Stats Database. Learn about usage guidelines and policies."
        keywords="terms of service, user agreement, pokemon database terms, usage policy"
        url="/terms"
      />

      <div className="container mx-auto px-4">
        <Breadcrumb items={breadcrumbItems} />

        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          className="max-w-4xl mx-auto"
        >
          <div className="bg-white rounded-lg shadow-lg p-8">
            <h1 className="text-4xl font-bold text-gray-900 mb-6">Terms of Service</h1>
            <p className="text-gray-600 mb-8">
              Last updated: January 1, 2024
            </p>

            <div className="prose max-w-none">
              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Acceptance of Terms</h2>
                <p className="text-gray-700 mb-4">
                  By accessing and using Pokemon Stats Database ("the Service"), you accept and agree to be bound by the terms and provision of this agreement. If you do not agree to abide by the above, please do not use this service.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Description of Service</h2>
                <p className="text-gray-700 mb-4">
                  Pokemon Stats Database is a web-based information service that provides comprehensive Pokemon data including statistics, abilities, types, and other related information. The service is provided free of charge for educational and entertainment purposes.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">3. Use License</h2>
                <p className="text-gray-700 mb-4">
                  Permission is granted to temporarily access and use Pokemon Stats Database for personal, non-commercial transitory viewing only. This is the grant of a license, not a transfer of title, and under this license you may not:
                </p>
                <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2">
                  <li>modify or copy the materials</li>
                  <li>use the materials for any commercial purpose or for any public display (commercial or non-commercial)</li>
                  <li>attempt to decompile or reverse engineer any software contained on the website</li>
                  <li>remove any copyright or other proprietary notations from the materials</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Data Usage and Accuracy</h2>
                <p className="text-gray-700 mb-4">
                  While we strive to provide accurate and up-to-date Pokemon information, we make no warranties about the completeness, reliability, and accuracy of this information. Any action you take upon the information on this website is strictly at your own risk, and we will not be liable for any losses and damages in connection with the use of our website.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Intellectual Property</h2>
                <p className="text-gray-700 mb-4">
                  Pokemon and all related characters, names, marks, emblems and images are registered trademarks of their respective owners including but not limited to Nintendo, Game Freak, and The Pokemon Company. This website is not affiliated with or endorsed by any of these entities.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">6. User Conduct</h2>
                <p className="text-gray-700 mb-4">
                  Users agree not to use the service to:
                </p>
                <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2">
                  <li>Violate any applicable local, state, national or international law</li>
                  <li>Transmit or distribute viruses, malware, or any other malicious code</li>
                  <li>Attempt to gain unauthorized access to our systems or networks</li>
                  <li>Interfere with or disrupt the service or servers connected to the service</li>
                  <li>Use automated systems to access the service in a manner that exceeds reasonable human use</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Privacy Policy</h2>
                <p className="text-gray-700 mb-4">
                  Your privacy is important to us. Please review our Privacy Policy, which also governs your use of the Service, to understand our practices regarding your personal information.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Disclaimer</h2>
                <p className="text-gray-700 mb-4">
                  The information on this website is provided on an "as is" basis. To the fullest extent permitted by law, this Company:
                </p>
                <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2">
                  <li>Excludes all representations and warranties relating to this website and its contents</li>
                  <li>Excludes all liability for damages arising out of or in connection with your use of this website</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Limitations of Liability</h2>
                <p className="text-gray-700 mb-4">
                  In no event shall Pokemon Stats Database or its suppliers be liable for any damages (including, without limitation, damages for loss of data or profit, or due to business interruption) arising out of the use or inability to use the materials on Pokemon Stats Database's website, even if Pokemon Stats Database or an authorized representative has been notified orally or in writing of the possibility of such damage.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">10. Modifications</h2>
                <p className="text-gray-700 mb-4">
                  Pokemon Stats Database may revise these terms of service at any time without notice. By using this website, you are agreeing to be bound by the then current version of these terms of service.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">11. Governing Law</h2>
                <p className="text-gray-700 mb-4">
                  These terms and conditions are governed by and construed in accordance with the laws of the jurisdiction in which Pokemon Stats Database operates, and you irrevocably submit to the exclusive jurisdiction of the courts in that jurisdiction.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">12. Contact Information</h2>
                <p className="text-gray-700 mb-4">
                  If you have any questions about these Terms of Service, please contact us at:
                </p>
                <p className="text-gray-700">
                  Email: legal@pokemonstats.example.com<br />
                  Website: <a href="/contact" className="text-blue-600 hover:text-blue-700 transition-colors">Contact Page</a>
                </p>
              </section>
            </div>
          </div>
        </motion.div>
      </div>
    </motion.div>
  );
}

export default TermsPage;